import {
  authOriginUIDs,
  authUIDs,
  purchaseSeatsTabUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { routes } from "@/global/routes";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { Box, Stack, Theme, Typography, darken } from "@mui/material";
import { useRouter } from "next/router";
import { AiOutlineDollarCircle } from "react-icons/ai";
import { useFrontendSubtotals } from "./useFrontendSubtotals";
import { useBackendSubtotals } from "./useBackendSubtotlals";
import { createBase64String } from "@/global/utils/auth.utils";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { usePaymentPlans } from "../Checkout/Payment/PaymentPlans/usePaymentPlans";
import { v4 as uuid } from "uuid";

export function TicketSubtotal() {
  const router = useRouter();
  const { lg: lgUp } = useCssMediaQuery("up");
  const purchaseSeatTab = router.query.purchaseSeatTab as string;
  const frontendSubtotals = useFrontendSubtotals();
  const backendSubtotals = useBackendSubtotals();
  const { paymentPlans, selectedPaymentPlan } = usePaymentPlans();

  // prettier-ignore
  function handleCalculateSubtotal(): number {
    if (paymentPlans.length > 0 && selectedPaymentPlan)
      return selectedPaymentPlan.installments[0].amount;
    if (backendSubtotals.serviceFeeTotalAmount > 0)
      return backendSubtotals.serviceFeeTotalAmount;
    return frontendSubtotals.ticketSubtotal + frontendSubtotals.perksSubtotal;
  }

  if (
    frontendSubtotals.ticketSubtotal <= 0 &&
    backendSubtotals.serviceFeeTotalAmount <= 0
  ) {
    return null;
  }

  return (
    <Box key={uuid()}>
      <Typography
        variant={lgUp ? "body1" : "body2"}
        fontWeight={"500"}
        color="text.secondary"
        noWrap
        lineHeight={1.3}
      >
        {purchaseSeatTab == purchaseSeatsTabUIDs.chooseSeats
          ? "Ticket Subtotal"
          : "Total"}
      </Typography>
      <Typography
        fontWeight={"500"}
        color="text.primary"
        fontSize={"1.2rem"}
        lineHeight={1.1}
        mt={0.3}
      >
        {getUSCurrency(handleCalculateSubtotal())}
      </Typography>
    </Box>
  );
}

export function OrderServiceFee() {
  const { lg: lgUp } = useCssMediaQuery("up");
  const backendSubtotals = useBackendSubtotals();

  if (backendSubtotals.orderServiceFee <= 0) return null;

  return (
    <Box>
      <Typography
        variant={lgUp ? "body1" : "body2"}
        fontWeight={"500"}
        color="text.secondary"
        noWrap
        lineHeight={1.3}
      >
        Processing Fee
      </Typography>
      <Typography
        fontWeight={"500"}
        color="text.primary"
        fontSize={"1.2rem"}
        lineHeight={1.1}
        mt={0.3}
      >
        {getUSCurrency(backendSubtotals.orderServiceFee)}
      </Typography>
    </Box>
  );
}

export function AvailableCredit(props: { amount: number }) {
  const { lg: lgUp } = useCssMediaQuery("up");
  const { theme } = useAppSystem();
  const styles = useStyles(theme);
  const { languages } = useTeamConfig();

  return (
    <Box p={1} sx={styles.availableCredit}>
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <Stack
          fontSize={"32px"}
          width={"32px"}
          height="32px"
          alignItems={"center"}
          color={"text.secondary"}
        >
          <AiOutlineDollarCircle />
        </Stack>

        <Box>
          <Typography
            variant={lgUp ? "body1" : "body2"}
            fontWeight={"500"}
            color="text.primary"
            noWrap
            lineHeight={1.1}
          >
            {languages?.availableCredit?.title || "Available Credit"}
          </Typography>
          <Typography
            fontWeight={"500"}
            color="text.primary"
            lineHeight={1.1}
            fontSize={"1.1rem"}
          >
            {getUSCurrency(props.amount)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export function AvailableCreditLogin() {
  const { lg: lgUp } = useCssMediaQuery("up");
  const { theme } = useAppSystem();
  const { teamSchema, platform, languages } = useTeamConfig();
  const styles = useStyles(theme);
  const { handleConnectTm } = useTicketmaster();
  const { handleLoginOkta } = useTicketsdotcom();
  const { shouldUseInternalLogin, handleInternalRedirect } = useInternalAuth();

  const handleLogin = async () => {
    let redirectState = {
      authOrigin: authOriginUIDs.generalAuth,
      pathname: routes.purchaseSeats,
      query: { teamSchema },
    };

    if (platform == vendorUUIDs.ticketmaster) {
      await handleConnectTm(redirectState);
    }

    if (platform == vendorUUIDs.tickesdotcom) {
      if (shouldUseInternalLogin) {
        const state = createBase64String(redirectState);
        return await handleInternalRedirect(routes.auth, state, authUIDs.login);
      }

      await handleLoginOkta(redirectState);
    }
  };

  return (
    <Box p={1} sx={styles.availableCredit}>
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <Stack
          fontSize={"32px"}
          width={"32px"}
          height="32px"
          alignItems={"center"}
          color={"text.primary"}
        >
          <AiOutlineDollarCircle />
        </Stack>

        <Box>
          <Typography
            variant={lgUp ? "body1" : "body2"}
            fontWeight={"500"}
            color="text.primary"
            noWrap
            lineHeight={1.1}
          >
            {languages?.availableCredit?.title || "Available Credit"}
          </Typography>
          <Box onClick={handleLogin} mt={0.4}>
            <Typography
              fontWeight={"500"}
              color="text.primary"
              lineHeight={1.1}
              variant={"body1"}
              fontSize={lgUp ? "0.8rem" : "0.7rem"}
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              {languages?.availableCredit?.loginText || "Login To View Credit"}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export function PaymentDue(props: { amount: number }) {
  const { lg: lgUp } = useCssMediaQuery("up");

  return (
    <Box>
      <Typography
        variant={lgUp ? "body1" : "body2"}
        fontWeight={"500"}
        color="text.secondary"
      >
        Due After Credit
      </Typography>
      <Typography
        fontWeight={"700"}
        color="text.primary"
        fontSize={"1.2rem"}
        lineHeight={1.1}
      >
        {getUSCurrency(props.amount)}
      </Typography>
    </Box>
  );
}

function useStyles(theme: Theme) {
  return {
    availableCredit: {
      backgroundColor: "#c8e6c9",
      border: `1px solid ${darken("#A9F3A7", 0.2)}`,
      borderRadius: "8px",
    },
  };
}
